import * as React from 'react';
import { cn } from '../cn.ts';

const control =
  'block w-full rounded-control border-0 bg-white px-3.5 py-2.5 text-base text-ink ring-1 ring-inset ring-line-strong placeholder:text-muted transition-shadow focus:outline-none focus:ring-2 focus:ring-brand disabled:bg-canvas aria-[invalid=true]:ring-bad-fg';

/** Label, control, hint and error wired together for screen readers. */
export function Field({ label, hint, error, children, className }: { label: React.ReactNode; hint?: React.ReactNode; error?: string | null; children: React.ReactElement; className?: string }) {
  const id = React.useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={id} className="block text-sm font-semibold text-ink">
        {label}
      </label>
      {hint && (
        <p id={hintId} className="text-sm text-mid">
          {hint}
        </p>
      )}
      {React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        id,
        'aria-describedby': [hintId, errId].filter(Boolean).join(' ') || undefined,
        'aria-invalid': error ? true : undefined,
      })}
      {error && (
        <p id={errId} className="text-sm font-medium text-bad-fg">
          {error}
        </p>
      )}
    </div>
  );
}

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({ className, ...props }, ref) => <input ref={ref} className={cn(control, className)} {...props} />);
Input.displayName = 'Input';

export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({ className, ...props }, ref) => <select ref={ref} className={cn(control, 'pr-8', className)} {...props} />);
Select.displayName = 'Select';

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...props }, ref) => <textarea ref={ref} className={cn(control, 'min-h-24', className)} {...props} />);
Textarea.displayName = 'Textarea';

/** Pounds input that reads and writes integer pence. */
export function MoneyInput({ pence, onPence, ...props }: { pence: number | null; onPence: (p: number | null) => void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [text, setText] = React.useState(pence == null ? '' : String(pence / 100));
  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-mid" aria-hidden>
        £
      </span>
      <Input
        inputMode="decimal"
        autoComplete="off"
        className="pl-7"
        value={text}
        onChange={(e) => {
          const v = e.target.value.replace(/[^\d.]/g, '');
          setText(v);
          const n = Number.parseFloat(v);
          onPence(Number.isFinite(n) ? Math.round(n * 100) : null);
        }}
        {...props}
      />
    </div>
  );
}
